import {
  TViewportResponse,
  TPkBlock,
  TViewportAccess,
  TViewportBuilding,
  TViewportGeoMarker,
  TViewportProtectionZoneSegment,
  TViewportSectorSegment,
  TViewportSubSector,
  TViewportSwitch,
  TViewportTrainStation,
} from '../types/viewport.type'
import {ESwitchType, type TSwitch} from '../types/project.type'
import {Axis, createAxis} from './axis'
import {groupByLine} from './groupZoneByLine'

/** The ends an axis switch is built from, whatever list the switch comes from. */
type TSwitchEnds = {
  id: string
  type: number
  kmPoint: number
  isActive: boolean
  trackAId: string | null
  trackBId: string | null
}

/**
 * One switch in the shape `createAxis` widens its cells for. The labels feed
 * the ruler's switch glyphs and are both or none: a glyph naming one end only
 * would read as a dead-end switch.
 */
export const toAxisSwitch = (
  sw: TSwitchEnds,
  trackLabel: (id: string | null) => string | null,
): TSwitch => {
  const a = trackLabel(sw.trackAId)
  const b = trackLabel(sw.trackBId)
  return {
    id: sw.id,
    type: sw.type as ESwitchType,
    kmPoint: sw.kmPoint,
    isActive: sw.isActive,
    trackLabels: a && b ? ([a, b] as [string, string]) : undefined,
  }
}

/**
 * The switches the axis widens its cells for, mapped from the viewport.
 *
 * The axis AND every lane's gaps/segments must be built from the SAME switch
 * list. A switch widens the PK cell it sits in (`calculateWidthSecteur`), so an
 * axis built with switches and lane geometry built without drift by a cell per
 * switch — the ranges slide off the ruler. Shared here so both call sites (the
 * data wrapper's axis, `useContextTracks`' lanes) build from one list.
 */
export const buildAxisSwitches = (viewport: TViewportResponse): TSwitch[] => {
  const trackLabel = (id: string | null): string | null => {
    if (!id) return null
    const track = viewport.tracks.find(candidate => candidate.id === id)
    return track ? (track.acronym ?? track.name) : null
  }
  return viewport.switches.map(sw => toAxisSwitch(sw, trackLabel))
}

/** Legacy track label hidden on the linear view until the migration drops it. */
const HIDDEN_LANE_LABEL = 'N/A'

/**
 * Turns the viewport response into ONE continuous linear view.
 *
 * Blocks are laid end to end on the project's own PK axis (`createAxis`, the
 * same scale `PKWrapper` rules), so a label runs through all of them as one lane
 * — a track stays continuous even where its `TrackSegment` changes. Rows are
 * global, never per block: an empty family in one block leaves a gap, not a
 * different row set.
 *
 * The two display levels still stack (plan 2c §4.3, §4.4): a lane is a TRACK and
 * survives empty, a sub-lane is a stacking artifact and is compacted.
 *
 * Every family keeps its own payload — placement only ADDS `startPoint`/`width`.
 * That is what lets the screen feed the same `render*` helpers the project frise
 * uses, instead of a flattened item that would have dropped `allSegments`,
 * access `type` or station colours on the way.
 */

/** PK step of the ruler, matching the existing linear view. */
export const DEFAULT_GRANULARITY = 50

/** A notion placed on the shared axis — the `{width, startPoint}` pair every `render*` helper expects. */
export type TPlaced<T> = T & {startPoint: number; width: number}

type TInterval = {kmStart: number; kmEnd: number}

/** A point notion, given the interval shape placement works on. */
type TAtPk<T> = T & TInterval

/** A block's slot on the shared axis, with the PK axis that rules it. */
export type TAxisBlock = {
  block: TPkBlock
  groupIndex: number
  left: number
  width: number
  axis: Axis
}

/** What sits between two consecutive blocks of the axis. */
export type TAxisBoundary = {
  left: number
  /**
   * The two touching PKs, as the ruler prints them at the seam: the left
   * block's rightmost label and the right block's leftmost one. Equal ends read
   * as continuous; differing ends ARE the jump, and a reversed count after the
   * seam IS the direction flip — no need to classify either, the numbers say it.
   */
  endPk: number
  startPk: number
  /** Null when the two blocks belong to different groups — no junction links them. */
  junction: {id: string; fromPk: number; toPk: number} | null
}

/**
 * Families drawn once across the whole axis (plan 2c §4.4): none of them can
 * discriminate a track, so splitting them per lane would repeat one fact N times.
 */
export type TLinearBands = {
  sectors: TPlaced<TViewportSectorSegment>[]
  subSectors: TPlaced<TViewportSubSector>[]
  trainStations: TPlaced<TViewportTrainStation>[]
  /** Sub-lanes, compacted by `groupByLine`. */
  geoMarkers: TPlaced<TViewportGeoMarker>[][]
  buildings: TPlaced<TViewportBuilding>[]
  /** Only the devices with NO declared pair — the others are drawn across lanes. */
  switches: TPlaced<TAtPk<TViewportSwitch>>[]
}

/** A run of the axis a lane has no geometry on. */
export type TLaneGap = {startPoint: number; width: number}

/**
 * A switch that declares the two lanes it joins, ready to be drawn ACROSS them.
 *
 * The x of each end is resolved here, on the axis; the y is not — it depends on
 * how tall the rows above happen to be, which only the DOM knows. The screen
 * measures that and pairs it with these two abscissas.
 */
export type TSwitchLink = {
  id: string
  type: number
  trackAId: string
  trackBId: string
  /** Centre of each end's PK, in pixels on the shared axis. */
  xA: number
  xB: number
  pkA: number
  pkB: number
}

/** One track, rendered even when empty — the lane IS the object displayed. */
export type TLaneRow = {
  trackId: string
  name: string
  acronym: string | null
  segments: {id: string; startPoint: number; width: number}[]
  /**
   * Where this track does NOT exist, in pixels on the shared axis.
   *
   * A lane spans the whole axis because a track is the object displayed, not a
   * consequence of what sits on it. Without this, "the track runs here and is
   * empty" and "there is no track here" draw identically — and on the REM, V3
   * exists on 2% of the axis.
   */
  gaps: TLaneGap[]
  /** Sub-lanes: the corridor comes from the track links, the sub-lane from `line`. */
  protectionZones: TPlaced<TViewportProtectionZoneSegment>[][]
  accesses: TPlaced<TAtPk<TViewportAccess>>[]
}

export type TLinearView = {
  width: number
  /** Height of the PK ruler, taken from the widest block's axis. */
  rulerHeight: number
  blocks: TAxisBlock[]
  boundaries: TAxisBoundary[]
  bands: TLinearBands
  laneRows: TLaneRow[]
  switchLinks: TSwitchLink[]
}

const ascending = (a: number, b: number): [number, number] => [
  Math.min(a, b),
  Math.max(a, b),
]

const intersects = (item: TInterval, range: TInterval) => {
  const [start, end] = ascending(item.kmStart, item.kmEnd)
  const [rangeStart, rangeEnd] = ascending(range.kmStart, range.kmEnd)
  return Math.max(start, rangeStart) <= Math.min(end, rangeEnd)
}

/** A point notion is a zero-length interval — give it one so it can be placed. */
const atPk = <T extends {kmPoint: number}>(item: T): TAtPk<T> => ({
  ...item,
  kmStart: item.kmPoint,
  kmEnd: item.kmPoint,
})

/**
 * Lay the blocks end to end, each on its own `Axis` at the SAME granularity.
 *
 * One axis per block rather than one for the whole viewport: PKs are not
 * continuous across a block boundary (the REM jumps 131500 → 200000), so a
 * single axis would draw the gap as distance. Same `echantillonValue` in each
 * keeps the scale uniform, which is what makes a lane read as one track.
 *
 * Built with NO switches by default. The project axis widens the cell a switch
 * sits in, but only when its PK lands exactly on a grid step — off-step switches
 * make `width()` and `grid()` disagree. On the linear view switches are drawn as
 * ordinary items in their band row, so the grid stays a plain PK ruler.
 *
 * `switches` exists for the ONE caller that must not change geometry: the
 * project screens, whose axis has always widened those cells. Feeding them the
 * same list keeps the composite axis pixel-identical to the project axis it
 * replaces — the widening hazard above is theirs already, and inherited rather
 * than introduced.
 *
 * The block's `direction` is honoured, not normalized away: `createAxis` reads
 * it off the argument ORDER, and it is what puts a junction on the seam. The REM
 * draws block 100k as 131500 → 100000 so it ends where block 200k starts, at the
 * PK the junction calls the same place.
 *
 * Each axis spans `block.drawn`, the stretch the viewport traverses, NOT the
 * block's own extent. The two differ: a block keeps its true bounds so a
 * junction can be matched on them, and because a segment has to be known to run
 * past the viewport edge to be drawn running past it.
 */
const layOutAxis = (
  viewport: TViewportResponse,
  granularity: number,
  switches: TSwitch[] = [],
): TAxisBlock[] => {
  const flat = viewport.groups.flatMap((group, groupIndex) =>
    group.blocks.map(block => ({block, groupIndex})),
  )
  let cursor = 0
  return flat.map(({block, groupIndex}) => {
    const [from, to] =
      block.direction === 'DESC'
        ? [block.drawn.kmEnd, block.drawn.kmStart]
        : [block.drawn.kmStart, block.drawn.kmEnd]
    const axis = createAxis(from, to, granularity, switches)
    const width = axis.totalWidth()
    const laid = {block, groupIndex, left: cursor, width, axis}
    cursor += width
    return laid
  })
}

/** Place a PK interval on the shared axis, via the block that holds it. */
const position = (
  item: TInterval,
  slot: TAxisBlock,
): {startPoint: number; width: number} => {
  const [start, end] = ascending(item.kmStart, item.kmEnd)
  // Clamped to what is DRAWN, not to the block: a notion straddling the viewport
  // edge would otherwise be measured on an axis that does not reach it.
  const clampedStart = Math.max(start, slot.block.drawn.kmStart)
  const clampedEnd = Math.min(end, slot.block.drawn.kmEnd)
  return {
    startPoint: slot.left + slot.axis.left(clampedStart, clampedEnd),
    // A point notion is a zero-length interval, which `width()` reports as 0.
    // It takes the width of its own PK cell instead, or it draws as nothing.
    width:
      clampedStart === clampedEnd
        ? slot.axis.widthAtPk(clampedStart)
        : slot.axis.width(clampedStart, clampedEnd),
  }
}

/**
 * Every item of a family, positioned across the whole axis.
 *
 * Exported so the week payload places its sections through the SAME code: the
 * activities and the axis they sit on come from two endpoints, and two
 * placement rules would let them disagree by a pixel that means a PK.
 */
export const placeOnAxis = <T extends TInterval>(
  items: T[],
  blocks: TAxisBlock[],
  keep: (item: T) => boolean = () => true,
): TPlaced<T>[] =>
  items.flatMap(item => {
    if (!keep(item)) return []
    const slot = blocks.find(candidate =>
      intersects(item, candidate.block.drawn),
    )
    if (!slot) return []
    return [{...item, ...position(item, slot)}]
  })

/**
 * Same, split into sub-lanes by `line`.
 *
 * Computed over the WHOLE axis, not per block: a sub-lane has to be one row from
 * end to end or the rows stop lining up between blocks.
 */
const placeByLine = <T extends TInterval & {line: number}>(
  items: T[],
  blocks: TAxisBlock[],
  keep: (item: T) => boolean = () => true,
): TPlaced<T>[][] =>
  groupByLine(placeOnAxis(items, blocks, keep)).map(([, subLane]) => subLane)

/**
 * The runs of the axis a lane's geometry leaves uncovered.
 *
 * Complement of the placed segments over [0, totalWidth], merged first: two
 * segments of one label inside a block can touch or overlap, and a naive walk
 * would emit a negative-width gap between them.
 */
const gapsBetween = (
  segments: {startPoint: number; width: number}[],
  totalWidth: number,
): TLaneGap[] => {
  const gaps: TLaneGap[] = []
  let cursor = 0
  for (const segment of [...segments].sort(
    (a, b) => a.startPoint - b.startPoint,
  )) {
    if (segment.startPoint > cursor) {
      gaps.push({startPoint: cursor, width: segment.startPoint - cursor})
    }
    cursor = Math.max(cursor, segment.startPoint + segment.width)
  }
  if (cursor < totalWidth) {
    gaps.push({startPoint: cursor, width: totalWidth - cursor})
  }
  return gaps
}

/**
 * The junction whose two PKs land one in each of the blocks meeting at a seam.
 *
 * Matched on the blocks' TRUE bounds, never on what the viewport draws: the REM's
 * `200000 ↔ 100000` seam is exactly the case where one end sits outside a
 * narrow viewport, and matching on the clipped range would report the two blocks
 * as unconnected — a red broken seam where the junction is the whole point.
 */
const junctionAtSeam = (
  viewport: TViewportResponse,
  previous: TAxisBlock,
  next: TAxisBlock,
) => {
  const holds = (block: TPkBlock, pk: number) =>
    intersects({kmStart: pk, kmEnd: pk}, block)
  return (
    viewport.junctions.find(
      candidate =>
        (holds(previous.block, candidate.pkA) &&
          holds(next.block, candidate.pkB)) ||
        (holds(previous.block, candidate.pkB) &&
          holds(next.block, candidate.pkA)),
    ) ?? null
  )
}

export const buildLinearView = (
  viewport: TViewportResponse,
  granularity: number = DEFAULT_GRANULARITY,
  switches: TSwitch[] = [],
): TLinearView => {
  const blocks = layOutAxis(viewport, granularity, switches)

  const boundaries: TAxisBoundary[] = blocks.slice(1).map((slot, index) => {
    const previous = blocks[index]
    const junction =
      slot.groupIndex === previous.groupIndex
        ? junctionAtSeam(viewport, previous, slot)
        : null
    // The label touching the seam: `drawn` is ascending, so a DESC block ends at
    // its low PK on the right, an ASC block starts at its low PK on the left.
    const endPk =
      previous.block.direction === 'DESC'
        ? previous.block.drawn.kmStart
        : previous.block.drawn.kmEnd
    const startPk =
      slot.block.direction === 'DESC'
        ? slot.block.drawn.kmEnd
        : slot.block.drawn.kmStart
    return {
      left: slot.left,
      endPk,
      startPk,
      junction: junction
        ? {id: junction.id, fromPk: junction.pkA, toPk: junction.pkB}
        : null,
    }
  })

  // A declared pair moves the device out of the band and onto the lanes it
  // joins. What is left in the band is what nobody has qualified yet.
  const declaresPair = (item: TViewportSwitch) =>
    item.trackAId !== null &&
    item.trackBId !== null &&
    item.trackAId !== item.trackBId

  const bands: TLinearBands = {
    sectors: placeOnAxis(viewport.sectorSegments, blocks),
    subSectors: placeOnAxis(viewport.subSectors, blocks),
    trainStations: placeOnAxis(viewport.trainStations, blocks),
    geoMarkers: placeByLine(viewport.geoMarkers, blocks),
    buildings: placeOnAxis(viewport.buildings, blocks),
    switches: placeOnAxis(
      viewport.switches.filter(item => !declaresPair(item)).map(atPk),
      blocks,
    ),
  }

  const switchLinks: TSwitchLink[] = viewport.switches
    .filter(declaresPair)
    .flatMap(item => {
      // Each end sits at its own PK, so each is placed on its own — a long
      // appliance is a slanted link, not a vertical tick.
      const pkA = item.pkA ?? item.kmPoint
      const pkB = item.pkB ?? item.kmPoint
      const [endA] = placeOnAxis([atPk({...item, kmPoint: pkA})], blocks)
      const [endB] = placeOnAxis([atPk({...item, kmPoint: pkB})], blocks)
      if (!endA || !endB) return []
      return [
        {
          id: item.id,
          type: item.type,
          trackAId: item.trackAId as string,
          trackBId: item.trackBId as string,
          xA: endA.startPoint + endA.width / 2,
          xB: endB.startPoint + endB.width / 2,
          pkA,
          pkB,
        },
      ]
    })

  // One lane per LABEL across the whole axis — a label present in two blocks is
  // one row, which is what keeps V1 continuous through a change of segment.
  const laneById = new Map<string, {name: string; acronym: string | null}>()
  for (const {block} of blocks) {
    for (const lane of block.lanes) {
      if (!laneById.has(lane.trackId)) {
        laneById.set(lane.trackId, {name: lane.name, acronym: lane.acronym})
      }
    }
  }

  const accesses = viewport.accesses.map(atPk)

  const totalWidth = blocks.reduce((sum, slot) => sum + slot.width, 0)

  const laneRows: TLaneRow[] = [...laneById.entries()]
    // Temporary: hide the legacy "N/A" track until the track-first migration
    // retires it. Front-only, applies to every linear-view consumer of laneRows.
    .filter(([, {name}]) => name.trim() !== HIDDEN_LANE_LABEL)
    .sort(([, a], [, b]) => a.name.localeCompare(b.name))
    .map(([trackId, {name, acronym}]) => {
      const onThisTrack = (item: {trackIds: string[]}) =>
        item.trackIds.includes(trackId)
      const segments = blocks.flatMap(slot =>
        (
          slot.block.lanes.find(lane => lane.trackId === trackId)?.segments ??
          []
        ).map(segment => ({id: segment.id, ...position(segment, slot)})),
      )
      return {
        trackId,
        name,
        acronym,
        segments,
        gaps: gapsBetween(segments, totalWidth),
        protectionZones: placeByLine(
          viewport.protectionZoneSegments,
          blocks,
          onThisTrack,
        ),
        accesses: placeOnAxis(accesses, blocks, onThisTrack),
      }
    })

  return {
    width: totalWidth,
    rulerHeight: blocks.reduce(
      (tallest, slot) => Math.max(tallest, slot.axis.heightPK),
      0,
    ),
    blocks,
    boundaries,
    bands,
    laneRows,
    switchLinks,
  }
}
